import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Mail, Save } from 'lucide-react';
import { adminEmailService } from '../../shared/services';

function mensagemDeErro(error: unknown, padrao: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    if (data?.message) return data.message;
  }
  return padrao;
}

function preview(html: string) {
  return html
    .replaceAll('{{nome}}', 'Maria da Silva')
    .replaceAll('{{trilha}}', 'Boas-vindas Grupo Amig3')
    .replaceAll('{{prazo}}', '30/10/2026')
    .replaceAll('{{validade}}', '1 hora')
    .replaceAll('{{link}}', '#');
}

export function AdminEmailsPage() {
  const queryClient = useQueryClient();
  const [selecionado, setSelecionado] = useState<string>('');
  const [assunto, setAssunto] = useState('');
  const [corpo, setCorpo] = useState('');
  const [nome, setNome] = useState('');
  const [ativo, setAtivo] = useState(true);
  const [aviso, setAviso] = useState<string | null>(null);

  const { data: templates = [] } = useQuery({
    queryKey: ['admin-emails'],
    queryFn: adminEmailService.listar,
  });

  const atual = templates.find((template) => template.chave === selecionado) ?? templates[0];

  useEffect(() => {
    if (!atual) return;
    setSelecionado(atual.chave);
    setNome(atual.nome);
    setAssunto(atual.assunto);
    setCorpo(atual.corpoHtml);
    setAtivo(atual.isActive);
  }, [atual?.chave]);

  const salvar = useMutation({
    mutationFn: () =>
      adminEmailService.atualizar(atual!.chave, {
        nome,
        assunto,
        corpoHtml: corpo,
        isActive: ativo,
      }),
    onSuccess: () => {
      setAviso('Template salvo.');
      queryClient.invalidateQueries({ queryKey: ['admin-emails'] });
    },
    onError: (error) => setAviso(mensagemDeErro(error, 'Não foi possível salvar o template.')),
  });

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Mail size={30} /> Templates de e-mail
          </h1>
          <p className="text-gray-600 mt-1">
            Edite o assunto e o HTML dos e-mails transacionais. Use variáveis como {'{{nome}}'} e {'{{link}}'}.
          </p>
        </div>

        {aviso && <div className="mb-4 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-800">{aviso}</div>}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <aside className="card lg:col-span-1 space-y-2">
            <h2 className="text-sm font-semibold text-gray-700 mb-2">Templates</h2>
            {templates.map((template) => (
              <button
                key={template.chave}
                type="button"
                onClick={() => setSelecionado(template.chave)}
                className={`w-full text-left rounded-lg border px-3 py-2 text-sm ${
                  atual?.chave === template.chave ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <p className="font-medium text-gray-900">{template.nome}</p>
                <p className="text-xs text-gray-500 font-mono">{template.chave}</p>
              </button>
            ))}
          </aside>

          {atual && (
            <form
              className="lg:col-span-3 space-y-6"
              onSubmit={(event) => {
                event.preventDefault();
                setAviso(null);
                salvar.mutate();
              }}
            >
              <div className="card grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Nome</span>
                  <input value={nome} onChange={(event) => setNome(event.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" />
                </label>
                <label className="flex items-center gap-2 mt-6">
                  <input type="checkbox" checked={ativo} onChange={(event) => setAtivo(event.target.checked)} />
                  <span className="text-sm text-gray-700">Ativo</span>
                </label>
                <label className="block md:col-span-2">
                  <span className="text-sm font-medium text-gray-700">Assunto</span>
                  <input value={assunto} onChange={(event) => setAssunto(event.target.value)} className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2" required />
                </label>
                <label className="block md:col-span-2">
                  <span className="text-sm font-medium text-gray-700">Corpo HTML</span>
                  <textarea
                    value={corpo}
                    onChange={(event) => setCorpo(event.target.value)}
                    rows={14}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-xs"
                    required
                  />
                </label>
                <div className="md:col-span-2">
                  <button type="submit" disabled={salvar.isPending} className="btn-primary flex items-center gap-2 disabled:opacity-50">
                    <Save size={16} /> {salvar.isPending ? 'Salvando...' : 'Salvar template'}
                  </button>
                </div>
              </div>

              <div className="card">
                <h2 className="text-sm font-semibold text-gray-700 mb-3">Pré-visualização</h2>
                <div className="rounded-lg border border-gray-200 p-4 bg-gray-50" dangerouslySetInnerHTML={{ __html: preview(corpo) }} />
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
